import React from 'react';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { useLocale } from 'locale';
import { IAgentOfficeBulletin, IAgentOfficeDealEnriched, ResolutionPreference } from 'utils/entities';
import PlaceholderIcon from 'assets/svg/developer/project-placeholder.svg';
import * as Styled from './styled';
import { UnitCardPrimaryParams, getAddress, getUnitCardPrimaryParams, renderAddress, unitCardTranslateKeys } from 'utils/addressFormatter';
import { PropertyDetailsSeparator } from 'ds/components/ListingCard';
import { DEFAULT_PLACEHOLDER_IMAGE } from 'utils/gallery';
import { Route } from 'config/routes';
import FavoritesToggler from 'components/favorites/FavoritesToggler';
import { useRoute } from 'react-router5';


export const AgentOfficeBulletinCard: React.FC<{ poi: IAgentOfficeBulletin | IAgentOfficeDealEnriched; isMapPreview?: boolean }> = ({ poi, isMapPreview }) => {
  const { t, formatMoney } = useLocale();
  const { route } = useRoute();
  const isOffice = route.name === Route.OfficePage;
  const constructImageURI = useImageUrlBuilder();
  const isCommercial = poi.type === 'commercialBulletin';

  let price;
  let imageSrc;
  let rooms;
  let area;
  let totalFloors;
  let address;
  let propertyTypeStr;
  const floor = poi.floor;
  if (poi.type === 'sold') {
    price = poi.declaredPrice ? formatMoney(poi.declaredPrice) : null;
    rooms = poi.numRooms ? t('listingCard.bedsCountLabel', { bedsCount: poi.numRooms }) : null;
    area = poi.grossArea ? t('widget.prices.size', { size: poi.grossArea }) : null;
    totalFloors = poi.totalFloors || '';
    address = getAddress<UnitCardPrimaryParams>(t, renderAddress)(poi.structuredAddress, unitCardTranslateKeys, poi.structuredAddress.resolutionPreferences, getUnitCardPrimaryParams);
    propertyTypeStr = t('agent.office.propertyType', { propertyType: poi.propertyType, isCommercial: false });
  }
  else {
    imageSrc = poi.images && poi.images.length ? constructImageURI(poi.images[0].imageUrl) : null;
    price = poi.price ? formatMoney(poi.price) : null;
    propertyTypeStr = t('agent.office.propertyType', { propertyType: poi.buildingClass, isCommercial: poi.type === 'commercialBulletin' });
    if (poi.type === 'commercialBulletin') {
      rooms = poi.rooms ? t('listingCard.bedsCountLabel', { bedsCount: poi.rooms }) : null;
    }
    else {
      rooms = poi.beds ? t('listingCard.bedsCountLabel', { bedsCount: poi.beds }) : null;
    }
    area = poi.area ? t('widget.prices.size', { size: poi.area }) : null;
    totalFloors = poi.floors || '';
    address = getAddress<UnitCardPrimaryParams>(t, renderAddress)(poi.structuredAddress, unitCardTranslateKeys, poi.structuredAddress.resolutionPreferences || ResolutionPreference.Accurate, getUnitCardPrimaryParams);
  }

  const fullFloor = floor !== null ? t('soldPage.floors', { floor, totalFloorNumber: Number(totalFloors) }) : null;
  const infoList = [ rooms, area, isMapPreview ? null : propertyTypeStr, fullFloor, address ].filter(Boolean);

  const placeholderImage = poi.type === 'sold' ? (
    <Styled.Image>
      <img
        src={constructImageURI(DEFAULT_PLACEHOLDER_IMAGE)}
        loading="lazy"
      />
    </Styled.Image>
  ) : (
    <Styled.PlaceholderWrapper isMapPreview={isMapPreview}>
      <PlaceholderIcon />
    </Styled.PlaceholderWrapper>
  );

  const skipClick = (e: React.SyntheticEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Styled.CardWrapper
      target="_blank"
      isMapPreview={isMapPreview}
      routeName={isCommercial ? Route.UnitPageCommercial : Route.UnitPage}
      routeParams={{ id: poi.id, tracking_event_source: `${isOffice ? 'office' : 'agent'}_page_active_listings` }}
      hideLinkParams
    >
      <Styled.ImgWrapper isMapPreview={isMapPreview}>
        {imageSrc ? (
          <Styled.Image>
            <img
              src={imageSrc}
              loading="lazy"
            />
          </Styled.Image>
        ) : placeholderImage}
        {poi.type === 'sold' ? null : (
          <Styled.SaveWrapper onClick={skipClick}>
            <FavoritesToggler id={poi.id} type={poi.type} tooltip={false} withButton noText isRound />
          </Styled.SaveWrapper>
        )}
        {isMapPreview ? (
          <Styled.MarketplaceTypeLabelWrapper isCommercial={isCommercial}>
            {t('listingCard.marketplaceTypeLabel', { isCommercial })}
          </Styled.MarketplaceTypeLabelWrapper>
        ) : null}
      </Styled.ImgWrapper>
      <Styled.InfoWrapper>
        <Styled.Price weight="bold">
          {price}
          {isMapPreview ? (
            <>
              <PropertyDetailsSeparator />
              <Styled.PropertyType weight="normal">{propertyTypeStr}</Styled.PropertyType>
            </>
          ) : null}
        </Styled.Price>
        <Styled.OtherInfo>{infoList.map((i, k) => (
          <React.Fragment key={k}>
            {k === 0 ? null : <PropertyDetailsSeparator />}
            {i}
          </React.Fragment>
        ))}</Styled.OtherInfo>
      </Styled.InfoWrapper>
    </Styled.CardWrapper>
  );
};
